import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next'; // Added for i18n
import { useAuth } from '../context/AuthContext';
import CardArt from './CardArt';
import { IconBulb, IconBooks } from './icons';
import { recordFlip, getFlipCounts, getTotalFlipCounts } from '../services/statsApi';
import { getCategoryIconNode } from '../utils/categoryIcon';
import colors from '../theme/colors';


// ===========================================
// ✨ CARD GLOW SETTINGS — tweak color/strength here
// ===========================================
const GLOW_COLOR = '255,250,235'; // warm white

const CARD_BORDER_GLOW = `0 0 20px 1px rgba(${GLOW_COLOR},0.5)`;
const CARD_BORDER_GLOW_ZOOMED_OUT = `0 0 24px 4px rgba(${GLOW_COLOR},0.33)`;
const STAR_GLOW = `drop-shadow(0 0 8px rgba(${GLOW_COLOR},0.2)) drop-shadow(0 0 1px rgba(${GLOW_COLOR},0.2))`;
const CIRCLE_GLOW = `0 0 18px 3px rgba(${GLOW_COLOR},0.9)`;

// Slightly lighter shade of themePrimary, used for the card frame + name banner so they match exactly
const themePrimaryHsl = colors.utils.rgbToHsl(colors.base.themePrimary);
const CARD_FRAME_COLOR = colors.utils.hslToRgb(themePrimaryHsl.h, themePrimaryHsl.s, themePrimaryHsl.l + 5);

// Flip card wrapper - provides 3D perspective
const CardWrapper = styled.div`
  width: 250px;
  height: 350px;
  perspective: 1000px;
  -webkit-perspective: 1000px;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  -webkit-user-select: none;
`;

// Inner card that flips
const CardInner = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  -webkit-transform-style: preserve-3d;
  transition: transform 0.6s ease;
  -webkit-transition: -webkit-transform 0.6s ease;
  transform: ${props => props.$isRevealed ? 'rotateY(180deg)' : 'rotateY(0deg)'};
  -webkit-transform: ${props => props.$isRevealed ? 'rotateY(180deg)' : 'rotateY(0deg)'};
`;

// Card Back — stone texture + 8-pointed star + center medallion
const CardBack = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  border-radius: 14px;
  overflow: hidden;

  /* Cracked stone look — site purple base with white crack lines */
  background-color: ${colors.base.themePrimary};
  background-image:
    linear-gradient(73deg,  transparent 47%, rgba(255,255,255,0.18) 47.5%, transparent 48%),
    linear-gradient(161deg, transparent 38%, rgba(255,255,255,0.14) 38.5%, transparent 39%),
    linear-gradient(210deg, transparent 55%, rgba(255,255,255,0.10) 55.5%, transparent 56%),
    linear-gradient(30deg,  transparent 68%, rgba(255,255,255,0.08) 68.5%, transparent 69%),
    radial-gradient(ellipse at 20% 25%, rgba(255,255,255,0.04) 0%, transparent 40%),
    radial-gradient(ellipse at 80% 75%, rgba(255,255,255,0.03) 0%, transparent 35%);

  /* Sharp white border — fully opaque */
  border: 5px solid rgba(255,255,255,0.9);
  box-shadow:
    inset 0 0 0 0px rgba(255,255,255,0.15),
    0 0 0 0px rgba(0,0,0,0.8),
    0 8px 28px rgba(0,0,0,0.7),
    ${CARD_BORDER_GLOW};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

// Card Front (shown after flip)
const CardFront = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform: rotateY(180deg) translateZ(1px);
  -webkit-transform: rotateY(180deg) translateZ(1px);
  border-radius: 12px;
`;

/* ── Hearthstone Card Front Components ──────────────────────────────── */

/* Outer frame — white, so only the card's white border/mat shows.
   Carries the same white streaks as the header, the modals and the card back,
   so the blue mat around the portrait and parchment isn't a flat block. */
const NodeContainer = styled(motion.div)`
  background-color: ${CARD_FRAME_COLOR};
  background-image:
    linear-gradient(73deg,  transparent calc(47% - 1px), rgba(255,255,255,0.14) 47%, transparent calc(47% + 1px)),
    linear-gradient(161deg, transparent calc(38% - 1px), rgba(255,255,255,0.10) 38%, transparent calc(38% + 1px)),
    linear-gradient(210deg, transparent calc(55% - 1px), rgba(255,255,255,0.08) 55%, transparent calc(55% + 1px)),
    linear-gradient(30deg,  transparent calc(68% - 1px), rgba(255,255,255,0.06) 68%, transparent calc(68% + 1px));
  border-radius: 14px;
  width: 250px;
  height: 350px;
  cursor: pointer;
  position: relative;
  overflow: visible;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  opacity: ${props => props.$isParentOnly ? 0.6 : 1};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  will-change: ${props => props.$isZoomedOut ? 'auto' : 'transform'};

  /* Sharp border — same cream-gold as the site title */
  border: 4px solid ${colors.base.themeSecondary};
  box-shadow: ${props => {
    if (props.$isZoomedOut) return `${CARD_BORDER_GLOW_ZOOMED_OUT}, 0 4px 14px rgba(0,0,0,0.45)`;
    const { r, g, b } = parseColorToRgb(props.$color || '#4A90E2');
    const selected = props.$isSelected;
    return `
      inset 0 0 0 0px rgba(0,0,0,0.4),
      0 0 0 0px rgba(0,0,0,0.7),
      0 12px 40px rgba(0,0,0,0.8),
      ${CARD_BORDER_GLOW}
      ${selected ? `, 0 0 22px rgba(${r},${g},${b},0.5)` : ''}
    `;
  }};

  ${props => props.$levelType === 'previous' && `opacity: 0.6; transform: scale(0.9);`}
  ${props => props.$levelType === 'next'     && `opacity: 0.8; transform: scale(0.95);`}
`;

/* Oval portrait window — off-white background, clips boss artwork */
const PortraitZone = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  height: 180px;
  border-radius: 48% 48% 44% 44% / 38% 38% 46% 46%;
  overflow: hidden;
  border: 1px solid ${colors.base.themeSecondary};
  box-shadow: 0 0 0 1px rgba(0,0,0,0.35), inset 0 0 14px rgba(0,0,0,0.12);
  /* Same pearlescent white as the description area */
  background: linear-gradient(135deg, #ffffff 0%, #f4f0ff 25%, #fff8fe 50%, #f0f8ff 75%, #ffffff 100%);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Subtle edge vignette */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at 50% 40%, transparent 50%, rgba(0,0,0,0.22) 100%);
    pointer-events: none;
  }

  > div {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    svg { width: 100%; height: 100%; filter: drop-shadow(0 3px 10px rgba(0,0,0,0.25)); }
  }
`;

/* Name banner — dark indigo scroll with sharp accent lines top + bottom */
const NameBanner = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 181px;
  height: 44px;
  background-color: ${CARD_FRAME_COLOR};
  background-image:
    linear-gradient(73deg,  transparent calc(47% - 1px), rgba(255,255,255,0.14) 47%, transparent calc(47% + 1px)),
    linear-gradient(161deg, transparent calc(38% - 1px), rgba(255,255,255,0.10) 38%, transparent calc(38% + 1px)),
    linear-gradient(210deg, transparent calc(55% - 1px), rgba(255,255,255,0.08) 55%, transparent calc(55% + 1px)),
    linear-gradient(30deg,  transparent calc(68% - 1px), rgba(255,255,255,0.06) 68%, transparent calc(68% + 1px));
  border-top: 1px solid ${colors.base.themeSecondary};
  border-bottom: 1px solid ${colors.base.themeSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 30px;
  z-index: 5;

  /* Small diamond corner accents */
  &::before, &::after {
    content: '◆';
    position: absolute;
    color: ${colors.base.themeSecondary};
    font-size: 8px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.8;
  }
  &::before { left: 10px; }
  &::after  { right: 10px; }
`;

/* Title text — white, no shadow */
/* The name banner is a fixed 44px, which holds two lines at the base size. A handful of
   titles — all French, the English set never overflows — wrap onto a third line and spill
   over the portrait above and the parchment below. Rather than truncate them, step the
   size down until greedy wrapping fits two lines.

   Measured with a canvas rather than the DOM so it costs nothing to run during render,
   and cached per title because the tree re-renders the same strings constantly. */
const BANNER_TEXT_WIDTH = 182; // 250px card - 2x4px border - 2x30px banner padding
const BANNER_HEIGHT = 44;      // NameBanner, which has no vertical padding
const TITLE_LINE_HEIGHT = 1.2; // must match NameText below
const TITLE_SIZES_EM = [0.92, 0.85, 0.78, 0.72, 0.66, 0.6];
const titleSizeCache = new Map();
let titleMeasureCtx = null;

const measureTitle = (text, sizeEm, rootPx, family) => {
  if (!titleMeasureCtx) {
    titleMeasureCtx = document.createElement('canvas').getContext('2d');
  }
  titleMeasureCtx.font = `700 ${sizeEm * rootPx}px ${family}`;
  return titleMeasureCtx.measureText(text).width;
};

// Greedy wrap, the same way the browser breaks on spaces
const linesAtSize = (words, sizeEm, rootPx, family) => {
  let lines = 1;
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (measureTitle(candidate, sizeEm, rootPx, family) <= BANNER_TEXT_WIDTH) {
      current = candidate;
    } else {
      lines += 1;
      current = word;
    }
  }
  return lines;
};

const fitTitleSize = (title) => {
  const text = (title || '').toUpperCase();
  if (!text) return TITLE_SIZES_EM[0];
  if (titleSizeCache.has(text)) return titleSizeCache.get(text);

  const bodyStyle = getComputedStyle(document.body);
  const rootPx = parseFloat(bodyStyle.fontSize) || 16;
  const family = bodyStyle.fontFamily || 'sans-serif';
  const words = text.split(/\s+/).filter(Boolean);
  const measured = TITLE_SIZES_EM.map((size) => ({
    size,
    lines: linesAtSize(words, size, rootPx, family),
  }));

  // Preferred: the largest size that still reads on two lines.
  let chosen = measured.find((m) => m.lines <= 2);
  // A few titles are simply too long for two lines at any readable size. For those the
  // real constraint is the banner height — three small lines fit, four never do — so fall
  // back to the largest size whose wrapped block stays inside the banner.
  if (!chosen) {
    chosen = measured.find(
      (m) => m.lines * m.size * rootPx * TITLE_LINE_HEIGHT <= BANNER_HEIGHT
    );
  }
  const size = chosen ? chosen.size : TITLE_SIZES_EM[TITLE_SIZES_EM.length - 1];
  titleSizeCache.set(text, size);
  return size;
};

const NameText = styled.h3`
  margin: 0;
  color: ${colors.base.themeTertiary};
  text-transform: uppercase;
  font-size: ${props => props.$sizeEm}em;
  font-weight: 700;
  text-align: center;
  line-height: 1.2;
  word-break: break-word;
  hyphens: auto;
`;

/* Description text area — pearlescent white background, black text */
const ParchmentBox = styled.div`
  position: absolute;
  top: 235px;
  left: 12px;
  right: 12px;
  bottom: 27px;
  background: linear-gradient(135deg, ${colors.base.themeTertiary} 0%, ${adjustColor(colors.base.themeTertiary, -10)} 50%, ${colors.base.themeTertiary} 100%);
  border-radius: 6px;
  border: 1px solid ${colors.base.themeSecondary};
  box-shadow: 0 0 0 1px rgba(0,0,0,0.3);
  padding: 4px 8px;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Guard: the box is a fixed 80px, so clip rather than let text spill onto
     the frame if a font renders taller than measured */
  overflow: hidden;
`;

/* Sized against the parchment's fixed height: 80px minus border and padding
   leaves ~70px, so 5 clamped lines cap the type at font-size * line-height
   <= 14px. Kept at 5 lines because descriptions run to 163 chars at the 90th
   percentile — dropping to 4 would truncate a tenth of the cards. */
const ParchmentText = styled.p`
  margin: 0;
  color: #1a1a1a;
  font-size: 0.64rem;
  line-height: 1.35;
  font-style: italic;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/* Category icon gem — top-left, balances the info corner at top-right.
   Clicking it jumps back up to the top-level category card (e.g. "Lifestyle
   Habits"), so it's a button rather than a decorative div. */
const CategoryGem = styled.button`
  position: absolute;
  top: 8px;
  left: 8px;
  width: 26px;
  height: 26px;
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  /* Pearlescent white background */
  background: linear-gradient(135deg, #ffffff 0%, #f0f0ff 30%, #fff8ff 60%, #f8ffff 100%);
  border: 1px solid rgba(255,255,255,0.9);
  box-shadow: 0 0 0 1px rgba(0,0,0,0.25), 0 2px 6px rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  overflow: hidden;
  transition: filter 0.15s ease;

  &:hover { filter: brightness(0.94); }
  &:active { filter: brightness(0.88); }

  > div {
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  svg { width: 100%; height: 100%; }
`;

// const NodeIcon = styled.div`
//   position: absolute;
//   top: 15px;
//   right: 45px;
//   width: 24px;
//   height: 24px;
//   background: rgba(255, 255, 255, 0.2);
//   border-radius: 50%;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   font-size: 12px;
//   z-index: 3;
// `;

// const HasChildrenIndicator = styled.div`
//   position: absolute;
//   bottom: 10px;
//   right: 15px;
//   color: rgba(255, 255, 255, 0.7);
//   font-size: 0.8rem;
//   font-weight: 500;
//   z-index: 3;
// `;

const FavoriteButton = styled.button`
  position: absolute;
  bottom: -22px;
  right: 10px;
  background: ${props => props.$isBookmarked
    ? 'linear-gradient(145deg, ' + colors.base.accentPrimary + ', ' + colors.accent.secondary + ')'
    : 'linear-gradient(145deg, rgba(60,60,70,0.9), rgba(40,40,50,0.9))'
  };
  border: 2px solid ${props => props.$isBookmarked
    ? 'rgba(255,220,100,0.6)'
    : 'rgba(255,255,255,0.2)'
  };
  border-radius: 50%;
  width: 42px;
  height: 42px;
  color: ${props => props.$isBookmarked ? '#1a1a2e' : 'rgba(255,255,255,0.8)'};
  font-size: 1.2rem;
  cursor: pointer;
  z-index: 15;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.$isBookmarked
    ? '0 3px 12px rgba(255,180,0,0.35)'
    : '0 3px 10px rgba(0,0,0,0.25)'
  };
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: ${props => props.$isBookmarked
      ? 'linear-gradient(145deg, ' + colors.accent.secondary + ', ' + colors.base.accentPrimary + ')'
      : 'linear-gradient(145deg, rgba(80,80,90,0.95), rgba(50,50,60,0.95))'
    };
    border-color: ${props => props.$isBookmarked ? 'rgba(255,230,120,0.8)' : 'rgba(255,255,255,0.4)'};
    color: ${props => props.$isBookmarked ? '#1a1a2e' : 'rgba(255,255,255,1)'};
    transform: scale(1.1);
    box-shadow: ${props => props.$isBookmarked
      ? '0 5px 15px rgba(255,180,0,0.4)'
      : '0 5px 14px rgba(0,0,0,0.3)'
    };
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ProgressButton = styled.button`
  position: absolute;
  bottom: -22px;
  left: 10px;
  background: ${props => props.$isCompleted
    ? `linear-gradient(145deg, ${colors.primary.darkRgba}, ${colors.base.themePrimary})`
    : 'linear-gradient(145deg, rgba(60,60,70,0.9), rgba(40,40,50,0.9))'
  };
  border: 2px solid ${props => props.$isCompleted
    ? 'rgba(255,255,255,0.35)'
    : 'rgba(255,255,255,0.2)'
  };
  border-radius: 50%;
  width: 42px;
  height: 42px;
  color: ${props => props.$isCompleted ? colors.base.accentPrimary : 'rgba(255,255,255,0.8)'};
  font-size: 1.2rem;
  cursor: ${props => props.$isParentNode ? 'not-allowed' : 'pointer'};
  z-index: 15;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.$isCompleted
    ? '0 3px 12px rgba(0,0,0,0.3)'
    : '0 3px 10px rgba(0,0,0,0.25)'
  };
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  opacity: ${props => props.$isParentNode ? 0.5 : 1};

  &:hover {
    background: ${props => props.$isParentNode
      ? (props.$isCompleted ? `linear-gradient(145deg, ${colors.primary.darkRgba}, ${colors.base.themePrimary})` : 'linear-gradient(145deg, rgba(60,60,70,0.9), rgba(40,40,50,0.9))')
      : (props.$isCompleted
        ? `linear-gradient(145deg, ${colors.base.themePrimaryVariant}, ${colors.primary.darkRgba})`
        : 'linear-gradient(145deg, rgba(80,80,90,0.95), rgba(50,50,60,0.95))')
    };
    border-color: ${props => props.$isParentNode
      ? 'rgba(255,255,255,0.2)'
      : 'rgba(255,255,255,0.4)'
    };
    transform: ${props => props.$isParentNode ? 'none' : 'scale(1.1)'};
    box-shadow: ${props => props.$isParentNode ? '0 3px 10px rgba(0,0,0,0.25)' : '0 5px 14px rgba(0,0,0,0.3)'};
  }

  &:active {
    transform: ${props => props.$isParentNode ? 'none' : 'scale(0.95)'};
  }
`;

const SolutionButton = styled.button`
  position: absolute;
  bottom: -22px;
  left: 54px;
  background: ${props => props.$isSolution
    ? 'linear-gradient(145deg, #22c55e, #16a34a)'
    : 'linear-gradient(145deg, rgba(60,60,70,0.9), rgba(40,40,50,0.9))'
  };
  border: 2px solid ${props => props.$isSolution
    ? 'rgba(100,255,150,0.4)'
    : 'rgba(255,255,255,0.2)'
  };
  border-radius: 50%;
  width: 42px;
  height: 42px;
  /* Lit warm-yellow bulb when the idea is selected, dim bulb otherwise */
  color: ${props => props.$isSolution ? '#ffe9a8' : 'rgba(255,255,255,0.8)'};
  font-size: 1rem;
  cursor: pointer;

  svg {
    ${props => props.$isSolution ? 'filter: drop-shadow(0 0 4px rgba(255,220,120,0.9));' : ''}
  }
  z-index: 15;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.$isSolution
    ? '0 3px 12px rgba(34,197,94,0.35)'
    : '0 3px 10px rgba(0,0,0,0.25)'
  };
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: ${props => props.$isSolution
      ? 'linear-gradient(145deg, #16a34a, #22c55e)'
      : 'linear-gradient(145deg, rgba(80,80,90,0.95), rgba(50,50,60,0.95))'
    };
    border-color: ${props => props.$isSolution ? 'rgba(120,255,170,0.6)' : 'rgba(255,255,255,0.4)'};
    transform: scale(1.1);
    box-shadow: ${props => props.$isSolution
      ? '0 5px 15px rgba(34,197,94,0.4)'
      : '0 5px 14px rgba(0,0,0,0.3)'
    };
  }

  &:active {
    transform: scale(0.95);
  }
`;

// Stat container for admin mode - shows flip counts
const StatContainer = styled.button`
  position: absolute;
  bottom: -22px;
  background: ${colors.button.aboutBg || 'linear-gradient(145deg, rgba(60,60,70,0.9), rgba(40,40,50,0.9))'};
  border: 1px solid ${colors.button.aboutBorder || 'rgba(255,255,255,0.2)'};
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  z-index: 15;
  color: ${colors.button.aboutText || 'rgba(255,255,255,0.9)'};
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: linear-gradient(145deg, rgba(80,80,90,0.95), rgba(50,50,60,0.95));
    border-color: rgba(255,255,255,0.4);
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const AnonymousStatContainer = styled(StatContainer)`
  left: 10px;
`;

const AuthenticatedStatContainer = styled(StatContainer)`
  right: 10px;
`;

/* Info hotspot — a folded-corner flap ("dog-ear") on the card's top-right.

   Geometry: it is offset -4px on both sides so it paints over the card's 4px
   white border instead of sitting inside it — that way its white runs straight
   into the border's white with no seam. Hence the flat #fff fill (any shading
   would read as a shadow against that border) and the 14px corner arc, which
   is the card's outer radius rather than its inner 10px. NodeContainer keeps
   overflow: visible, so painting over the border works. The fold is the
   quadratic from (0,0) to (59,59), bowed ~3px towards the card centre for the
   paper give; it clears the portrait oval entirely. */
const InfoCorner = styled.button`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 59px;
  height: 59px;
  padding: 0;
  border: none;
  cursor: pointer;
  z-index: 10;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  /* Stays an invisible hotspot where clip-path: path() is unsupported, rather
     than painting a full rectangle over the portrait */
  background: transparent;
  transition: filter 0.2s ease;

  @supports (clip-path: path('M 0 0 Z')) {
    background: ${colors.base.themeSecondary};
    clip-path: path('M 0 0 L 45 0 A 14 14 0 0 1 59 14 L 59 59 Q 25 34, 0 0 Z');
  }

  &:hover  { filter: brightness(0.94); }
  &:active { filter: brightness(0.88); }
`;

/* Books glyph — same icon as the modal's "Sources" section, so the corner
   reads as "opens the sources" rather than a generic info mark. Centered on
   the flap's actual visible area: the flap is clipped to a folded-corner
   shape (see InfoCorner's clip-path above) whose visible centroid sits at
   roughly (38, 21) in the 59x59 box, not the box's own center — top/right
   below were derived from that centroid, not eyeballed. */
const InfoCornerGlyph = styled(IconBooks)`
  position: absolute;
  top: 12px;
  right: 12px;
  color: #334155;
  pointer-events: none;
`;

const ExpandButton = styled.button`
  position: absolute;
  bottom: -22px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(145deg, ${colors.primary.darkRgba}, ${adjustColor(colors.primary.darkRgba, -20)});
  border: 2px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  width: 42px;
  height: 42px;
  color: ${colors.text.white};
  font-size: 1.1rem;
  cursor: pointer;
  z-index: 15;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 10px rgba(0,0,0,0.25);
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: linear-gradient(145deg, ${colors.base.themePrimaryVariant}, ${adjustColor(colors.base.themePrimaryVariant, -15)});
    border-color: rgba(255,255,255,0.5);
    transform: translateX(-50%) scale(1.1);
    box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  }

  &:active {
    transform: translateX(-50%) scale(0.95);
  }
`;



// Helper functions - support both hex (#fff) and rgb(r,g,b) formats
function parseColorToRgb(color) {
  if (!color) return { r: 128, g: 128, b: 128 };

  // Handle rgb/rgba format
  if (color.startsWith('rgb')) {
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      return { r: parseInt(match[1]), g: parseInt(match[2]), b: parseInt(match[3]) };
    }
  }

  // Handle hex format
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16)
    };
  }

  return { r: 128, g: 128, b: 128 };
}

function adjustColor(color, amount) {
  const { r, g, b } = parseColorToRgb(color);
  const newR = Math.max(0, Math.min(255, r + amount));
  const newG = Math.max(0, Math.min(255, g + amount));
  const newB = Math.max(0, Math.min(255, b + amount));
  return `rgb(${newR}, ${newG}, ${newB})`;
}

// const getIconEmoji = (iconName) => {
//   const icons = {
//     bed: '🛏️',
//     medical: '🏥',
//     brain: '🧠',
//     lifestyle: '💪',
//     room: '🏠',
//     clock: '⏰',
//     routine: '📝',
//     disorder: '😴',
//     pain: '🤕',
//     hormone: '⚗️',
//     anxiety: '😰',
//     depression: '😔',
//     stress: '🎯',
//     nutrition: '🥗',
//     exercise: '🏃',
//     technology: '📱',
//     temperature: '🌡️',
//     light: '💡',
//     sound: '🔇',
//     mattress: '🛏️',
//     bedtime: '🌙',
//     circadian: '☀️',
//     nap: '😊',
//     apnea: '😮',
//     restless: '🦵',
//     narcolepsy: '😪',
//     worry: '😟',
//     panic: '😱',
//     trauma: '💔',
//     caffeine: '☕',
//     dinner: '🍽️',
//     foods: '🍎'
//   };
//   return icons[iconName] || '🔹';
// };

// Fixed-size card-back title text — same font size on every card regardless
// of title length, wraps normally within its zone instead of stretching to fill it.
const BackTitleText = styled.div`
  width: 230px;
  text-align: center;
  color: ${colors.base.themeTertiary};
  font-weight: 800;
  font-size: 22px;
  letter-spacing: 0.5px;
  line-height: 1.15;
  text-transform: uppercase;
  overflow: hidden;
`;

const TreeNode = ({
  node,
  isSelected,
  onClick,
  onDetailsClick,
  levelType = 'current',
  opacity = 1,
  isParentOnly = false,
  hasChildren = false,
  isExpanded = false,
  onToggleExpansion = null,
  showExpandButton = false,
  onProgressToggle = null,
  completedNodes = new Set(), // Set of completed node IDs for real-time updates
  allNodes = [], // All nodes for building parent chain
  zoomLevel = 1, // Current zoom level for performance optimization
  isAdmin = false, // Admin mode for showing flip stats
  onShowStats = null // Callback to show world map modal
}) => {
  const { t } = useTranslation(); // Added for i18n
  const { isAuthenticated, trackNodeVisit, toggleBookmark, revealedNodes: contextRevealedNodes, revealNode: contextRevealNode, solutions, toggleSolution } = useAuth();
  const [isBookmarked, setIsBookmarked] = useState(false);

  // Flip stats for admin mode
  const [flipStats, setFlipStats] = useState({ anonymousCount: 0, authenticatedCount: 0 });

  // Track if card has been revealed (flipped)
  // When authenticated: use context (database-backed)
  // When not authenticated: use localStorage
  const [localRevealed, setLocalRevealed] = useState(() => {
    // Check localStorage for revealed state (fallback for non-authenticated users)
    const localRevealedNodes = JSON.parse(localStorage.getItem('revealedNodes') || '[]');
    return localRevealedNodes.includes(node.id);
  });

  // Determine revealed state based on authentication
  const isRevealed = isAuthenticated
    ? (contextRevealedNodes ? contextRevealedNodes.has(node.id) : false)
    : localRevealed;

  // Use completedNodes prop directly instead of local state for real-time updates
  const isCompleted = completedNodes ? completedNodes.has(node.id) : false;

  // Performance optimization: detect if zoomed out
  // Landscape gets a more lenient threshold to keep cards visible at low zoom
  const isMobile = typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) <= 768;
  const isLandscape = typeof window !== 'undefined' && window.innerHeight < window.innerWidth;
  const isMobileLandscape = isMobile && isLandscape;
  
  // Threshold: landscape 0.3 (very lenient for 0.5 zoom), mobile portrait 0.7 (aggressive), desktop 0.5
  const zoomThreshold = isMobileLandscape ? 0.2:  (isMobile ? 0.2 : 0);
  const isZoomedOut = zoomLevel < zoomThreshold;

  const checkBookmarkStatus = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await fetch(`/api/auth/check/${node.id}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsBookmarked(data.bookmarked || false);
      }
    } catch (error) {
      console.error('Failed to check bookmark status:', error);
    }
  }, [isAuthenticated, node.id]);

  useEffect(() => {
    if (isAuthenticated) {
      // Check bookmark status
      checkBookmarkStatus();
    }
  }, [isAuthenticated, node.id, checkBookmarkStatus]);

  // Fetch flip stats in admin mode
  // Root node (level 0) shows TOTAL stats across all nodes
  const isRootNode = node.level === 0;

  useEffect(() => {
    if (isAdmin) {
      if (isRootNode) {
        // Root node: fetch total stats across all nodes
        getTotalFlipCounts().then(stats => {
          setFlipStats(stats);
        });
      } else if (!isParentOnly) {
        // Regular node: fetch per-node stats
        getFlipCounts(node.id).then(stats => {
          setFlipStats(stats);
        });
      }
    }
  }, [isAdmin, node.id, isParentOnly, isRootNode]);

  // Handle card flip reveal
  const handleReveal = useCallback(() => {
    if (!isRevealed) {
      // Record the flip for stats (both anonymous and authenticated)
      recordFlip(node.id);

      if (isAuthenticated && contextRevealNode) {
        // Save to database when authenticated
        contextRevealNode(node.id);
      } else {
        // Save to localStorage when not authenticated
        setLocalRevealed(true);
        const localRevealedNodes = JSON.parse(localStorage.getItem('revealedNodes') || '[]');
        if (!localRevealedNodes.includes(node.id)) {
          localRevealedNodes.push(node.id);
          localStorage.setItem('revealedNodes', JSON.stringify(localRevealedNodes));
        }
      }
    }
  }, [isRevealed, node.id, isAuthenticated, contextRevealNode]);

  // Navigation: déplace l'arbre vers ce nœud
  const handleNavigate = (e) => {
    e.preventDefault();

    // If not revealed yet, flip the card first
    if (!isRevealed) {
      handleReveal();
      return; // Don't navigate on first click, just flip
    }

    // Track the visit for navigation
    if (isAuthenticated) {
      trackNodeVisit(node.id, 'CLICK');
    }

    // Navigation uniquement
    onClick(node);
  };

  // Affichage des détails: ouvre la fiche sans naviguer
  const handleShowDetails = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Track the detail view
    if (isAuthenticated) {
      trackNodeVisit(node.id, 'VIEW');
    }
    
    // Affichage des détails uniquement
    if (onDetailsClick) {
      onDetailsClick(node);
    }
  };

    const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      alert(t('tree.pleaseSignIn'));
      return;
    }

    try {
      const result = await toggleBookmark(node.id);
      if (result) {
        setIsBookmarked(result.bookmarked);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const isSolution = solutions ? solutions.some(s => s.node_id === node.id) : false;
  const solutionCount = solutions ? solutions.length : 0;
  const canAddSolution = isSolution || solutionCount < 5;

  const handleSolutionClick = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      alert(t('tree.pleaseSignIn'));
      return;
    }
    try {
      await toggleSolution(node.id, {
        title: node.title,
        color_code: node.colorCode || node.themedColor
      });
    } catch (error) {
      console.error('Failed to toggle solution:', error);
    }
  };

  const handleProgressClick = async (e) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      alert(t('tree.pleaseSignIn'));
      return;
    }

    try {
      const response = await fetch(`/api/auth/progress/${node.id}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ completed: !isCompleted })
      });

      if (response.ok) {
        const data = await response.json();

        // Call the callback to update parent state for real-time XP bar update
        if (onProgressToggle) {
          // Update the current node
          onProgressToggle(node.id, data.completed);

          // Update all affected parent nodes
          if (data.affectedParents && data.affectedParents.length > 0) {
            data.affectedParents.forEach(parent => {
              onProgressToggle(parent.nodeId, parent.completed);
            });
          }
        }
      }
    } catch (error) {
      console.error('Failed to toggle progress:', error);
    }
  };

  // Handle arrow click: expand/collapse + focus
  const handleExpandClick = (e) => {
    e.stopPropagation();

    // Toggle expansion
    if (onToggleExpansion) {
      onToggleExpansion(node.id);
    }
  };

  // Use themed color (from theme) or fall back to database colorCode
  const nodeColor = node.themedColor || node.colorCode;
  // Category node used for the small category icon gem
  const categoryIconNode = getCategoryIconNode(node, allNodes);

  return (
    <CardWrapper onClick={handleNavigate}>
      <CardInner $isRevealed={isRevealed}>
        {/* ── Card Back — cracked stone + 8-pointed star + medallion ── */}
        <CardBack $color={nodeColor}>

          {/* Node title — split in half so it never overlaps the star: first half sits
              in the zone above it, second half in the zone below it. */}
          {(() => {
            const words = (node.title || '').split(' ').filter(Boolean);
            const splitAt = Math.ceil(words.length / 2);
            const topText = words.slice(0, splitAt).join(' ');
            const bottomText = words.slice(splitAt).join(' ');
            const zoneStyle = {
              position: 'absolute', left: 0, right: 0, zIndex: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none', overflow: 'hidden',
            };
            return (
              <>
                {/* Zone above the star — text hugs its bottom edge, closer to the star */}
                <div style={{ ...zoneStyle, top: 16, height: 78, alignItems: 'flex-end' }}>
                  <BackTitleText>{topText}</BackTitleText>
                </div>
                {/* Zone below the star — text hugs its top edge, closer to the star */}
                <div style={{ ...zoneStyle, top: 256, height: 60, alignItems: 'flex-start' }}>
                  <BackTitleText>{bottomText}</BackTitleText>
                </div>
              </>
            );
          })()}

          {/* 8-pointed star: two overlapping diamond shapes */}
          <div style={{ position: 'relative', width: '150px', height: '150px', flexShrink: 0, zIndex: 1, filter: STAR_GLOW }}>

            {/* Diamond 1 — site button purple (N S E W points) */}
            <div style={{
              position: 'absolute', inset: '10px',
              clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
              background: colors.base.themePrimary,
              boxShadow: '2px 4px 14px rgba(0,0,0,0.7)',
            }} />

            {/* Diamond 2 — same purple, rotated 45° (NE SE SW NW points) */}
            <div style={{
              position: 'absolute', inset: '10px',
              clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
              transform: 'rotate(45deg)',
              background: colors.base.themePrimary,
              boxShadow: '2px 4px 14px rgba(0,0,0,0.6)',
            }} />

            {/* Outer ring */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '78px', height: '78px', borderRadius: '50%',
              background: colors.base.themePrimary,
              border: '2px solid rgba(255,255,255,0.35)',
            }} />

            {/* Center medallion — site title gold color, white border, category icon */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60px', height: '60px', borderRadius: '50%',
              background: '#ffffff',
              border: '2px solid rgba(255,255,255,0.88)',
              boxShadow: `0 4px 14px rgba(0,0,0,0.7), inset 0 1px 3px rgba(255,255,255,0.12), ${CIRCLE_GLOW}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
            }}>
              <div style={{ width: '100%', height: '100%' }}>
                <CardArt id={categoryIconNode.id} title={categoryIconNode.title} />
              </div>
            </div>
          </div>

          {/* Click to reveal hint */}
          <div style={{
            position: 'absolute', bottom: 10, zIndex: 1,
            color: 'rgba(180,175,200,0.55)', fontSize: '0.68em',
            letterSpacing: '0.04em',
          }}>
            {t('tree.clickToReveal') || 'Click to reveal'}
          </div>
        </CardBack>

        {/* ── Card Front — Hearthstone style ── */}
        <CardFront>
          <NodeContainer
            $isSelected={isSelected}
            $color={nodeColor}
            $levelType={levelType}
            $isParentOnly={isParentOnly}
            $isZoomedOut={isZoomedOut}
            whileTap={{ scale: isZoomedOut ? 1 : 0.98 }}
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            transition={{ duration: isZoomedOut ? 0 : 0.2, ease: "easeOut" }}
          >
            {/* Oval portrait window with boss artwork */}
            {!isZoomedOut && (
              <PortraitZone $color={nodeColor}>
                <CardArt id={node.id} title={node.title} />
              </PortraitZone>
            )}

            {/* Name banner — dark scroll, sharp gold top+bottom lines */}
            <NameBanner>
              <NameText $sizeEm={fitTitleSize(node.title)}>{node.title}</NameText>
            </NameBanner>

            {/* Parchment description area */}
            {!isZoomedOut && (node.shortDescription || node.description) && (
              <ParchmentBox>
                <ParchmentText>{node.shortDescription || node.description}</ParchmentText>
              </ParchmentBox>
            )}

            {/* Category icon gem — top-left, balances the info corner.
                Jumps back up to the top-level category card (e.g. "Lifestyle
                Habits"), not just the immediate parent. */}
            {!isZoomedOut && (
              <CategoryGem
                $color={nodeColor}
                onClick={(e) => {
                  e.stopPropagation();
                  onClick(categoryIconNode);
                }}
                title={t('tree.goToCategory')}
                aria-label={t('tree.goToCategory')}
              >
                <CardArt id={categoryIconNode.id} title={categoryIconNode.title} />
              </CategoryGem>
            )}

            {/* Info hotspot — folded top-right corner. Leaf cards only: sources,
                specialists and the AI text now only exist on leaf nodes, so a
                parent/category card has nothing left for this to open. */}
            {!hasChildren && (
              <InfoCorner
                onClick={handleShowDetails}
                title={t('tree.viewDetails')}
                aria-label={t('tree.viewDetails')}
              >
                <InfoCornerGlyph size={18} />
              </InfoCorner>
            )}

            {/* Admin mode: show stat containers instead of progress/favorite buttons */}
            {/* Root node shows TOTAL stats, other nodes show per-node stats */}
            {isAdmin && (!isParentOnly || isRootNode) && (
              <>
                <AnonymousStatContainer
                  onClick={(e) => {
                    e.stopPropagation();
                    // Pass 'total' for root node to show total stats in modal
                    if (onShowStats) onShowStats(isRootNode ? 'total' : node.id, isRootNode ? t('admin.totalStats') || 'Total Stats' : node.title, 'anonymous');
                  }}
                  title={isRootNode ? (t('admin.totalAnonymousFlips') || 'Total anonymous flips') : (t('admin.anonymousFlips') || 'Anonymous flips')}
                >
                  👤 {flipStats.anonymousCount}
                </AnonymousStatContainer>
                <AuthenticatedStatContainer
                  onClick={(e) => {
                    e.stopPropagation();
                    // Pass 'total' for root node to show total stats in modal
                    if (onShowStats) onShowStats(isRootNode ? 'total' : node.id, isRootNode ? t('admin.totalStats') || 'Total Stats' : node.title, 'authenticated');
                  }}
                  title={isRootNode ? (t('admin.totalAuthenticatedFlips') || 'Total authenticated flips') : (t('admin.authenticatedFlips') || 'Authenticated flips')}
                >
                  🔐 {flipStats.authenticatedCount}
                </AuthenticatedStatContainer>
              </>
            )}

            {/* Normal mode: show progress/favorite buttons */}
            {!isAdmin && isAuthenticated && !isParentOnly && !hasChildren && (
              <ProgressButton
                $isCompleted={isCompleted}
                onClick={handleProgressClick}
                title={isCompleted ? t('tree.markAsIncomplete') : t('tree.markAsComplete')}
              >
                ✗
              </ProgressButton>
            )}

            {!isAdmin && isAuthenticated && !isParentOnly && (
              <FavoriteButton
                $isBookmarked={isBookmarked}
                onClick={handleFavoriteClick}
                title={isBookmarked ? t('tree.removeFromFavorites') : t('tree.addToFavorites')}
              >
                {isBookmarked ? '★' : '☆'}
              </FavoriteButton>
            )}

            {!isAdmin && isAuthenticated && !isParentOnly && !hasChildren && canAddSolution && (
              <SolutionButton
                $isSolution={isSolution}
                onClick={handleSolutionClick}
                title={isSolution ? t('tree.removeFromSolutions') : t('tree.addToSolutions')}
              >
                <IconBulb size={20} />
              </SolutionButton>
            )}

            {/* Expand button */}
            {showExpandButton && hasChildren && !isParentOnly && (
              <ExpandButton
                $isExpanded={isExpanded}
                onClick={handleExpandClick}
                title={isExpanded ? t('tree.collapseChildren') : t('tree.expandChildren')}
              >
                {isExpanded ? '▲' : '▼'}
              </ExpandButton>
            )}
          </NodeContainer>
        </CardFront>
      </CardInner>
    </CardWrapper>
  );
};

// Memoize TreeNode to prevent unnecessary re-renders
// Only re-render if node props actually change
export default React.memo(TreeNode);
